import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getOrder,
  getGasStation,
  getCustomer,
  getAddress,
  listDeliveryDrivers,
  assignDriver,
  updateOrderStatus,
  confirmCashPayment,
  updateOrderPaymentMethod,
  listPaymentClients,
  type UserOrganization,
  type PaymentClientSummary,
} from '@vn-propane/shared/client';
import type { Order, DeliveryDriver, ErrorResponse, OrderStatus, PaymentMethod } from '@vn-propane/shared/client';
import { ArrowLeft, User, Store, Truck, Package, Clock, CheckCircle, CheckCircle2, XCircle, Banknote, CreditCard, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatPrice } from '@/lib/utils';
import { DeliveryMap, type DriverMarkerData, type GasStationMarkerData, type CustomerMarkerData } from '@/components/DeliveryMap';
import { useRealtimeStore } from '@/stores';

type OutletContext = {
  organization: UserOrganization;
};

export default function OrderDetail() {
  const { id: orgId, orderId } = useParams<{ id: string; orderId: string }>();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const realtimeStore = useRealtimeStore();
  const [order, setOrder] = useState<Order | null>(null);
  const [drivers, setDrivers] = useState<DeliveryDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showCashConfirm, setShowCashConfirm] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [confirmingCash, setConfirmingCash] = useState(false);
  const [switchingPayment, setSwitchingPayment] = useState(false);
  const [hasOnlinePayment, setHasOnlinePayment] = useState(false);

  // Map data
  const [gasStationMarker, setGasStationMarker] = useState<GasStationMarkerData | null>(null);
  const [customerMarker, setCustomerMarker] = useState<CustomerMarkerData | null>(null);
  const [driverMarker, setDriverMarker] = useState<DriverMarkerData | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!orgId || !orderId) return;

    try {
      const result = await getOrder({
        path: { org_id: orgId, id: orderId },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 404) {
          setError(t('orders.orderNotFound'));
          return;
        }
        setError(errorResponse.error?.message || t('orders.failedToLoadOrder'));
        return;
      }

      if (result.data) {
        setOrder(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('orders.failedToLoadOrder'));
      console.error(err);
    }
  }, [orgId, orderId, t]);

  const fetchDrivers = useCallback(async () => {
    if (!orgId) return;

    try {
      const result = await listDeliveryDrivers({
        path: { org_id: orgId },
      });

      if (result.data) {
        setDrivers(result.data.data);
      }
    } catch (err) {
      console.error('Failed to load drivers:', err);
    }
  }, [orgId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchOrder(), fetchDrivers()]);
      // Fetch payment clients to determine if online payment is available
      if (orgId) {
        try {
          const result = await listPaymentClients({ path: { org_id: orgId } });
          if (result.data) {
            const enabled = result.data.data.filter((c: PaymentClientSummary) => c.isEnabled);
            setHasOnlinePayment(enabled.length > 0);
          }
        } catch {
          // Silently fail - just won't allow switching to online
        }
      }
      setLoading(false);
    };
    load();
  }, [fetchOrder, fetchDrivers]);

  // Fetch gas station and customer addresses for the map
  useEffect(() => {
    if (!order || !orgId) return;

    const loadMapData = async () => {
      // Fetch gas station and its address
      try {
        const gsResult = await getGasStation({
          path: { org_id: orgId, id: order.gas_station_id },
        });
        if (gsResult.data) {
          const gs = gsResult.data.data;
          if (gs.addressId) {
            const addrResult = await getAddress({
              path: { org_id: orgId, id: gs.addressId },
            });
            if (addrResult.data?.data) {
              const addr = addrResult.data.data;
              if (addr.latitude && addr.longitude) {
                setGasStationMarker({
                  id: gs.id,
                  name: gs.name,
                  latitude: addr.latitude,
                  longitude: addr.longitude,
                });
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to load gas station for map:', err);
      }

      // Fetch customer and their address
      try {
        const custResult = await getCustomer({
          path: { org_id: orgId, id: order.customer_id },
        });
        if (custResult.data) {
          const cust = custResult.data.data;
          if (cust.addressId) {
            const addrResult = await getAddress({
              path: { org_id: orgId, id: cust.addressId },
            });
            if (addrResult.data?.data) {
              const addr = addrResult.data.data;
              if (addr.latitude && addr.longitude) {
                setCustomerMarker({
                  id: cust.id,
                  name: cust.fullName,
                  latitude: addr.latitude,
                  longitude: addr.longitude,
                  address: addr.formattedAddress,
                });
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to load customer for map:', err);
      }
    };

    loadMapData();
  }, [order, orgId]);

  // Get driver location from realtime store
  useEffect(() => {
    if (!order?.driver_id) {
      setDriverMarker(null);
      return;
    }

    const updateDriverLocation = () => {
      const driverLocation = realtimeStore.getDriverLocation(order.driver_id!);
      if (driverLocation) {
        setDriverMarker({
          id: driverLocation.driver_id,
          name: driverLocation.driver_name || order.driver_name || 'Driver',
          latitude: driverLocation.latitude,
          longitude: driverLocation.longitude,
          isOnDelivery: !!driverLocation.current_order_id,
          lastUpdated: driverLocation.timestamp,
        });
      }
    };

    // Initial load
    updateDriverLocation();

    // Subscribe to updates
    const unsubscribe = realtimeStore.onDriverLocation((location) => {
      if (location.driver_id === order.driver_id) {
        setDriverMarker({
          id: location.driver_id,
          name: location.driver_name || order.driver_name || 'Driver',
          latitude: location.latitude,
          longitude: location.longitude,
          isOnDelivery: !!location.current_order_id,
          lastUpdated: location.timestamp,
        });
      }
    });

    return () => unsubscribe();
  }, [order?.driver_id, order?.driver_name, realtimeStore]);

  const handleAssignDriver = async (driverId: string) => {
    if (!orgId || !orderId) return;

    setAssigning(true);
    setActionError(null);
    try {
      const result = await assignDriver({
        path: { org_id: orgId, id: orderId },
        body: { driver_id: driverId },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setActionError(errorResponse.error?.message || t('orders.failedToAssignDriver'));
        return;
      }

      await fetchOrder();
    } catch (err) {
      setActionError(t('orders.failedToAssignDriver'));
      console.error(err);
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusChange = async (status: OrderStatus) => {
    if (!orgId || !orderId) return;

    setUpdatingStatus(true);
    setActionError(null);
    try {
      const result = await updateOrderStatus({
        path: { org_id: orgId, id: orderId },
        body: { status },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setActionError(errorResponse.error?.message || t('orders.failedToUpdateStatus'));
        return;
      }

      await fetchOrder();
    } catch (err) {
      setActionError(t('orders.failedToUpdateStatus'));
      console.error(err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConfirmCash = async (amount?: number) => {
    if (!orgId || !orderId || !order) return;

    const confirmAmount = amount ?? parseFloat(cashAmount);
    if (isNaN(confirmAmount) || confirmAmount <= 0) {
      setActionError(t('orders.failedToConfirmCash'));
      return;
    }

    setConfirmingCash(true);
    setActionError(null);
    try {
      const result = await confirmCashPayment({
        path: { org_id: orgId, id: orderId },
        body: { amount: confirmAmount },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setActionError(errorResponse.error?.message || t('orders.failedToConfirmCash'));
        return;
      }

      setShowCashConfirm(false);
      setCashAmount('');
      await fetchOrder();
    } catch (err) {
      setActionError(t('orders.failedToConfirmCash'));
      console.error(err);
    } finally {
      setConfirmingCash(false);
    }
  };

  const handleSwitchPaymentMethod = async (method: PaymentMethod) => {
    if (!orgId || !orderId || !order || order.payment_method === method) return;

    setSwitchingPayment(true);
    setActionError(null);
    try {
      const result = await updateOrderPaymentMethod({
        path: { org_id: orgId, id: orderId },
        body: { payment_method: method },
      });
      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setActionError(errorResponse.error?.message || t('orders.failedToUpdateStatus'));
        return;
      }
      await fetchOrder();
    } catch {
      setActionError(t('orders.failedToUpdateStatus'));
    } finally {
      setSwitchingPayment(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const statusConfig: Record<OrderStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; icon: typeof Clock }> = {
    pending: { label: t('orders.statusPending'), variant: 'secondary', icon: Clock },
    assigned: { label: t('orders.statusAssigned'), variant: 'default', icon: Truck },
    in_progress: { label: t('orders.statusInProgress'), variant: 'default', icon: Truck },
    arrived: { label: t('orders.statusArrived'), variant: 'default', icon: Truck },
    delivered: { label: t('orders.statusDelivered'), variant: 'outline', icon: CheckCircle },
    completed: { label: t('orders.statusCompleted'), variant: 'outline', icon: CheckCircle2 },
    cancelled: { label: t('orders.statusCancelled'), variant: 'destructive', icon: XCircle },
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common:buttons.back')}
        </Button>
        <div className="text-red-500 p-4">{error}</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common:buttons.back')}
        </Button>
        <div className="text-muted-foreground p-4">{t('orders.orderNotFound')}</div>
      </div>
    );
  }

  const config = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = config.icon;

  const hasMapData = gasStationMarker || customerMarker || driverMarker;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/organizations/${orgId}/orders`}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('orders.title')}
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
            {t('orders.orderNumber', { number: order.id.slice(-8).toUpperCase() })}
          </h2>
          <p className="text-muted-foreground">
            {t('orders.created')} {formatDate(order.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.variant} className="flex items-center gap-1 text-sm px-3 py-1">
            <StatusIcon className="w-4 h-4" />
            {config.label}
          </Badge>
        </div>
      </div>

      {actionError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <div className={`flex flex-col ${hasMapData ? 'xl:flex-row' : ''} gap-6`}>
        {/* Left side - Order details */}
        <div className={`space-y-6 ${hasMapData ? 'xl:flex-1' : ''}`}>
          <div className="grid gap-6 md:grid-cols-2">
            {/* Customer & Station Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('orders.orderDetails')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('orders.customer')}</p>
                    <p className="font-medium">{order.customer_name || t('orders.unknown')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Store className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('orders.gasStation')}</p>
                    <p className="font-medium">{order.gas_station_name || t('orders.unknown')}</p>
                  </div>
                </div>
                {order.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('orders.notes')}</p>
                    <p className="text-sm bg-muted p-2 rounded">{order.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Driver Assignment & Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('orders.delivery')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">{t('orders.assignedDriver')}</p>
                  {order.driver_id ? (
                    <div className="flex items-center gap-2">
                      <Truck className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">{order.driver_name}</span>
                    </div>
                  ) : (
                    <Select
                      disabled={assigning}
                      onValueChange={handleAssignDriver}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('orders.selectDriver')} />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map((driver) => (
                          <SelectItem key={driver.id} value={driver.id}>
                            {driver.display_name || driver.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">{t('orders.status')}</p>
                  {order.status === 'completed' || order.status === 'cancelled' ? (
                    <p className="font-medium">{config.label}</p>
                  ) : (
                    <Select
                      value={order.status}
                      disabled={updatingStatus}
                      onValueChange={(v) => handleStatusChange(v as OrderStatus)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('orders.statusPending')}</SelectItem>
                        <SelectItem value="assigned">{t('orders.statusAssigned')}</SelectItem>
                        <SelectItem value="in_progress">{t('orders.statusInProgress')}</SelectItem>
                        <SelectItem value="arrived">{t('orders.statusArrived')}</SelectItem>
                        <SelectItem value="delivered">{t('orders.statusDelivered')}</SelectItem>
                        <SelectItem value="completed">{t('orders.statusCompleted')}</SelectItem>
                        <SelectItem value="cancelled">{t('orders.statusCancelled')}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Confirmed - green alert for completed orders */}
          {order.status === 'completed' && (
            <div className="rounded-lg border-2 border-green-400 bg-green-50 dark:bg-green-950/30 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-800 dark:text-green-200">
                    {t('orders.paymentConfirmed')}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {order.payment_method === 'cash' ? t('orders.cashPaymentConfirmed') : t('orders.onlinePaymentConfirmed')}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-green-700 dark:text-green-300 text-lg">
                    {formatPrice(order.total_amount.toString(), organization.currency)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cash Collection Alert - prominent orange alert for cash orders */}
          {order.payment_method === 'cash' && order.status !== 'completed' && order.status !== 'cancelled' && (
            <div className="rounded-lg border-2 border-orange-400 bg-orange-50 dark:bg-orange-950/30 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-orange-100 dark:bg-orange-900/40 border-b border-orange-300">
                <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-800 dark:text-orange-200">
                    {t('orders.cashCollection')}
                  </h3>
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    {t('orders.cashCollectionDescription')}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-orange-700 dark:text-orange-300 text-lg">
                    {formatPrice(order.total_amount.toString(), organization.currency)}
                  </div>
                </div>
              </div>
              <div className="p-4">
                {!showCashConfirm ? (
                  <Button onClick={() => setShowCashConfirm(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                    <Banknote className="w-4 h-4 mr-2" />
                    {t('orders.confirmCash')}
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-orange-800 dark:text-orange-200">
                      {t('orders.confirmCashDescription')}
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="cash-amount" className="text-orange-800 dark:text-orange-200">{t('orders.amountCollected')}</Label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          id="cash-amount"
                          type="number"
                          value={cashAmount}
                          onChange={(e) => setCashAmount(e.target.value)}
                          placeholder={order.total_amount.toString()}
                          className="bg-white dark:bg-background"
                        />
                        <Button
                          variant="outline"
                          disabled={confirmingCash}
                          onClick={() => handleConfirmCash(order.total_amount)}
                          className="whitespace-nowrap"
                        >
                          {t('orders.confirmFullAmount')} ({formatPrice(order.total_amount.toString(), organization.currency)})
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        disabled={confirmingCash || !cashAmount}
                        onClick={() => handleConfirmCash()}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        {confirmingCash ? t('common:labels.loading') : t('orders.confirmCash')}
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={confirmingCash}
                        onClick={() => { setShowCashConfirm(false); setCashAmount(''); }}
                      >
                        {t('common:buttons.cancel')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Method Card - shown for non-completed/non-cancelled orders */}
          {order.status !== 'completed' && order.status !== 'cancelled' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {t('orders.paymentMethod')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => hasOnlinePayment && handleSwitchPaymentMethod('online')}
                    disabled={switchingPayment || !hasOnlinePayment}
                    className={`flex-1 p-3 rounded-md border text-center transition-colors flex items-center justify-center gap-2 text-sm ${
                      !hasOnlinePayment
                        ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                        : order.payment_method === 'online'
                          ? 'border-primary bg-primary/5 font-medium'
                          : 'border-border hover:border-primary/50 text-muted-foreground'
                    }`}
                  >
                    <CreditCard className="w-4 h-4" />
                    {t('orders.onlinePayment')}
                  </button>
                  <button
                    onClick={() => handleSwitchPaymentMethod('cash')}
                    disabled={switchingPayment}
                    className={`flex-1 p-3 rounded-md border text-center transition-colors flex items-center justify-center gap-2 text-sm ${
                      order.payment_method === 'cash'
                        ? 'border-primary bg-primary/5 font-medium'
                        : 'border-border hover:border-primary/50 text-muted-foreground'
                    }`}
                  >
                    <Banknote className="w-4 h-4" />
                    {t('orders.cashOnDelivery')}
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                {t('orders.items')} ({order.items.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {order.items.map((item) => (
                  <div key={item.id} className="py-3 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{item.product_name || item.product_id}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.quantity} x {formatPrice(item.unit_price.toString(), organization.currency)}
                      </p>
                    </div>
                    <div className="font-medium">
                      {formatPrice(item.subtotal.toString(), organization.currency)}
                    </div>
                  </div>
                ))}
                <div className="py-3 flex justify-between items-center font-bold">
                  <span>{t('orders.total')}</span>
                  <span>{formatPrice(order.total_amount.toString(), organization.currency)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right side - Delivery Map */}
        {hasMapData && (
          <div className="xl:w-[28rem] xl:shrink-0">
            <Card className="xl:sticky xl:top-4">
              <CardHeader>
                <CardTitle className="text-lg">{t('orders.deliveryMap')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DeliveryMap
                  gasStations={gasStationMarker ? [gasStationMarker] : []}
                  customers={customerMarker ? [customerMarker] : []}
                  drivers={driverMarker ? [driverMarker] : []}
                  className="h-64 xl:h-80 rounded-lg overflow-hidden"
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
